import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  User,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { auth, db } from '../firebase/config';
import { doc, setDoc } from 'firebase/firestore';
import { generateJoinCode } from '../utils/joinCode';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signup: (email: string, password: string, name: string, skipHousehold?: boolean, plan?: 'free' | 'premium') => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Signup
  async function signup(email: string, password: string, name: string, skipHousehold: boolean = false, plan: 'free' | 'premium' = 'free') {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const userId = userCredential.user.uid;
    
    // Only create household if not skipping (i.e., not accepting an invitation)
    let householdId = null;
    
    if (!skipHousehold) {
      // Create default household FIRST
      householdId = `household_${userId}`;
      
      // Calculate trial end date (30 days from now for free users)
      const trialEndsAt = new Date();
      trialEndsAt.setDate(trialEndsAt.getDate() + 30);
      
      await setDoc(doc(db, 'households', householdId), {
        name: `${name}'s Household`,
        ownerId: userId,
        members: [userId],
        joinCode: generateJoinCode(),
        createdAt: new Date().toISOString(),
        subscriptionStatus: plan === 'premium' ? 'pending' : 'inactive', // 'pending' will be updated by Stripe webhook
        subscriptionPlan: plan,
        trialEndsAt: plan === 'free' ? trialEndsAt.toISOString() : null,
      });
    }

    // Create user document in Firestore WITH household link (if not skipping)
    await setDoc(doc(db, 'users', userId), {
      email,
      displayName: name,
      householdId: householdId,  // Will be null if accepting invitation
      createdAt: new Date().toISOString(),
    });
  }

  // Login
  async function login(email: string, password: string) {
    await signInWithEmailAndPassword(auth, email, password);
  }

  // Logout
  async function logout() {
    await signOut(auth);
  }

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    user,
    loading,
    signup,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
